import axios from "axios";
import {
  CreatePaymentConfigurationInput,
  PaymentConfiguration,
  PaymentDocumentType,
  RequestPaymentInput,
  TaxIdRecord,
  Transaction
} from "../types/payments";

const PAYMENTS_API = process.env.REACT_APP_PAYMENTS_API_URL;
const WEB_API = process.env.REACT_APP_API_URL;

const authHeaders = (authToken: string) => ({
  headers: { Authorization: `Bearer ${authToken}` }
});

export const getPlans = async ({
  status,
  type
}: {
  status: "active" | "non-active";
  type: "consumable" | "subscription";
}) => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/plan?status=${status}&type=${type}`
  );
  return data;
};

export const postPurchase = async ({
  email,
  plan,
  authToken
}: {
  email: string;
  plan: string;
  authToken: string;
}) => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/purchase`,
    {
      skuId: plan,
      email
    },
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data;
};

export const getPurchaseService = async ({
  transactionId,
  authToken
}: {
  transactionId: string;
  authToken: string;
}) => {
  if (!authToken) return;

  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/purchase/${transactionId}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data;
};

export const getPurchaseHistory = async ({
  authToken
}: {
  authToken: string;
}) => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/purchase/history/user`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data;
};

export const getCobrancasHistory = async ({
  authToken,
  accountId,
  page,
  dataIn,
  search,
  dataEnd,
  status,
  minPrice,
  maxPrice,
  sortBy,
  sortField,
  rowsPerPage
}: {
  authToken: string;
  accountId: string;
  page: number;
  dataIn?: string;
  dataEnd?: string;
  search?: string;
  status?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: string;
  sortField?: string;
  rowsPerPage: number;
}) => {
  const startAt = dataIn ? `&start_at=${dataIn}` : ``;
  const endAt = dataEnd ? `&end_at=${dataEnd}` : ``;
  const statusQuery = status ? `&status=${status}` : ``;
  const minPriceQuery = minPrice ? `&min_price=${minPrice}` : ``;
  const maxPriceQuery = maxPrice ? `&max_price=${maxPrice}` : ``;
  const sortByQuery = sortBy ? `&sort_dir=${sortBy}` : ``;
  const sortFieldQuery = sortField ? `&sort=${sortField}` : ``;
  const searchFieldQuery = search ? `&search=${search}` : ``;

  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/transaction?per_page=${rowsPerPage}&page=${page}&issuer=${accountId}${startAt}${endAt}${statusQuery}${minPriceQuery}${maxPriceQuery}${sortByQuery}${sortFieldQuery}${searchFieldQuery}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data;
};

export const getPaidTransactionsHistory = async ({
  authToken,
  accountId,
  dataIn,
  dataEnd
}: {
  authToken: string;
  accountId: string;
  dataIn?: string;
  dataEnd?: string;
}) => {
  const startAt = dataIn ? `&start_at=${dataIn}` : ``;
  const endAt = dataEnd ? `&end_at=${dataEnd}` : ``;

  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/transaction?&issuer=${accountId}${startAt}${endAt}&status=paid&time_base=paid_at`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data.items;
};

export const getDownloadFileCommissionsHistory = async ({
  authToken,
  dateIn,
  dateEnd
}: {
  authToken: string;
  dateIn?: string;
  dateEnd?: string;
}) => {
  const startAt = dateIn ? `&start_at=${dateIn}` : ``;
  const endAt = dateEnd ? `&data_end=${dateEnd}` : ``;

  const { data } = await axios.get(
    `${process.env.REACT_APP_PAYMENTS_API_URL}/transaction/file/commissions?${startAt}${endAt}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    }
  );
  return data;
};

/* -------------------------------------------------------------------------
 * Payment configurations (Easypay onboarding)
 *
 * Errors are intentionally left to propagate to the caller so the UI can show
 * what actually failed.
 * ---------------------------------------------------------------------- */

/**
 * Creates a payment configuration.
 *
 * The API expects `paymentId` wrapped in an array. That is a server side bug,
 * but it is the current contract, so it is kept inside this function and never
 * exposed to callers.
 */
export const createPaymentConfiguration = async ({
  authToken,
  input
}: {
  authToken: string;
  input: CreatePaymentConfigurationInput;
}): Promise<{ id: string }> => {
  const { data } = await axios.post(
    `${PAYMENTS_API}/config`,
    { ...input, paymentId: [input.paymentId] },
    authHeaders(authToken)
  );
  return data;
};

/**
 * Lists the configurations of a professional.
 *
 * Note that GET /config/{x} serves two different meanings on the same path:
 * with a paymentId it lists configurations, with a configId it addresses a
 * single one. They are different ids — do not mix them up.
 */
export const getPaymentConfigurations = async ({
  authToken,
  paymentId
}: {
  authToken: string;
  paymentId: string;
}): Promise<PaymentConfiguration[]> => {
  try {
    const { data } = await axios.get(
      `${PAYMENTS_API}/config/${paymentId}`,
      authHeaders(authToken)
    );
    return data;
  } catch (err) {
    // A professional with no configurations is answered with 404 rather than an
    // empty list. That is not a failure to report — it is the ordinary state of
    // everyone who has not registered yet, and the screens read it as "nothing
    // here", which is what puts the registration prompt in front of them.
    if (axios.isAxiosError(err) && err.response?.status === 404) return [];
    throw err;
  }
};

/**
 * Patches a configuration. Used both for the tax authority credentials and for
 * flagging the KYC documents as ready.
 *
 * The id travels in the path only and must not be present in the body.
 */
export const updatePaymentConfiguration = async ({
  authToken,
  configId,
  patch
}: {
  authToken: string;
  configId: string;
  patch: Partial<Omit<PaymentConfiguration, "id">>;
}) => {
  const { id, ...body } = patch as Partial<PaymentConfiguration>;
  const { data } = await axios.put(
    `${PAYMENTS_API}/config/${configId}`,
    body,
    authHeaders(authToken)
  );
  return data;
};

/* -------------------------------------------------------------------------
 * KYC documents
 * ---------------------------------------------------------------------- */

/** Separator used by the API to pack the S3 URL and its policy into one string. */
const UPLOAD_URL_SEPARATOR = "||||";

export interface DocumentUploadTarget {
  /** The S3 endpoint the multipart form is posted to. */
  url: string;
  /** Presigned POST policy fields that must precede the file in the form. */
  fields: Record<string, string>;
}

/**
 * Asks the API where to upload a document.
 *
 * The `url` it returns is not a URL: it is the S3 endpoint and the presigned
 * POST policy joined by "||||". This unpacks it into something usable.
 *
 * The document is addressed afterwards by the `id` sent here — the confirm and
 * delete routes take that same value. It is worth stating because it was not
 * always so: confirming an upload used to answer
 * `404 document not found with documentId: …`, which was a server-side type
 * mismatch on the id, fixed there rather than worked around here.
 *
 * The policy's `Key` is a different thing and is not that id: it is the S3
 * object key. On a completed configuration the document reads `id: 355a7134-…`
 * against `url: …/test.scribo.kyc/a4979460-…`. Do not reach for it when the
 * document id is what is wanted.
 */
export const requestDocumentUploadUrl = async ({
  authToken,
  configId,
  documentId,
  documentType,
  contentType,
  idGroup
}: {
  authToken: string;
  configId: string;
  /** UUID v4 generated on the client before this call. */
  documentId: string;
  documentType: PaymentDocumentType;
  contentType: string;
  idGroup: string;
}): Promise<DocumentUploadTarget> => {
  const { data } = await axios.put(
    `${PAYMENTS_API}/config/${configId}/documents`,
    { documentType, contentType, id: documentId, configId, idGroup },
    authHeaders(authToken)
  );

  const [url, policy] = String(data.url).split(UPLOAD_URL_SEPARATOR);
  return { url, fields: JSON.parse(policy) };
};

/**
 * Uploads the file to S3 with a presigned POST policy.
 *
 * This request goes straight to S3, so it must not carry the Auth0 header. The
 * Content-Type is left unset on purpose so the browser adds the multipart
 * boundary itself, and `file` has to be the last field of the form.
 */
export const uploadDocumentToStorage = async ({
  target,
  file,
  onProgress
}: {
  target: DocumentUploadTarget;
  file: File;
  onProgress?: (percentage: number) => void;
}) => {
  const form = new FormData();
  Object.entries(target.fields).forEach(([key, value]) =>
    form.append(key, value)
  );
  form.append("file", file);

  await axios.post(target.url, form, {
    onUploadProgress: event => {
      if (!onProgress || !event.total) return;
      onProgress(Math.round((event.loaded * 100) / event.total));
    }
  });
};

/**
 * Confirms a document was stored. Only call this once the upload to S3 has
 * actually succeeded, otherwise the KYC submission ends up referencing a
 * document that was never uploaded.
 */
export const markDocumentStored = async ({
  authToken,
  configId,
  documentId
}: {
  authToken: string;
  configId: string;
  documentId: string;
}) => {
  const { data } = await axios.post(
    `${PAYMENTS_API}/config/${configId}/documents/${documentId}`,
    { stored: true },
    authHeaders(authToken)
  );
  return data;
};

export const deleteDocument = async ({
  authToken,
  configId,
  documentId
}: {
  authToken: string;
  configId: string;
  documentId: string;
}) => {
  const { data } = await axios.delete(
    `${PAYMENTS_API}/config/${configId}/documents/${documentId}`,
    authHeaders(authToken)
  );
  return data;
};

/* -------------------------------------------------------------------------
 * Transactions
 * ---------------------------------------------------------------------- */

export const requestPayment = async ({
  authToken,
  input
}: {
  authToken: string;
  input: RequestPaymentInput;
}) => {
  const { data } = await axios.post(
    `${PAYMENTS_API}/transaction`,
    input,
    authHeaders(authToken)
  );
  return data;
};

export const getTransactionDetails = async ({
  authToken,
  transactionId
}: {
  authToken: string;
  transactionId: string;
}): Promise<Transaction> => {
  const { data } = await axios.get(
    `${PAYMENTS_API}/transaction/${transactionId}`,
    authHeaders(authToken)
  );
  return data;
};

/** Global Scribo commission, returned as a fraction (0.05 means 5%). */
export const getPaymentCommission = async ({
  authToken
}: {
  authToken: string;
}): Promise<{ percentage: number }> => {
  const { data } = await axios.get(
    `${PAYMENTS_API}/transaction/config/scribo-percentage`,
    authHeaders(authToken)
  );
  return data;
};

/** Checks whether Magnifinance is able to invoice for this fiscal id. */
export const getInvoiceProcessorReady = async ({
  authToken,
  fiscalId
}: {
  authToken: string;
  fiscalId: string;
}) => {
  const { data } = await axios.get(
    `${PAYMENTS_API}/transaction/magnifinance/validate-partner/${fiscalId}`,
    authHeaders(authToken)
  );
  return data;
};

/**
 * Looks up a company by its tax id, to prefill the configuration form.
 * This one lives on the web API, not on the payments API.
 *
 * An unknown tax id answers 404, which is a normal outcome here rather than a
 * failure, so it resolves to null. Prefilling is best effort and must never
 * block the form.
 */
export const getTaxIdInformation = async ({
  authToken,
  taxId
}: {
  authToken: string;
  taxId: string;
}): Promise<TaxIdRecord | null> => {
  try {
    const { data } = await axios.get(
      `${WEB_API}/api/professionals/tax_id/${taxId}`,
      authHeaders(authToken)
    );
    return data || null;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};
